import { useEffect, useState } from 'react';
import {
  historicoDaCampanha, MOTIVOS_DE_PARADA, type CampanhaCompleta, type LinhaDoHistorico,
} from '../../api/cptu';
import { dataDoDia, dataHora, faixaProfundidade, numero, plural, rotulo } from '../../formato';
import { degrauDe, SITUACAO_DA_CAMPANHA_CPTU, SITUACAO_DA_VERTICAL_CPTU } from '../../situacoes';
import { Carregando, ErroDaApi, Vazio } from '../../ui';

/**
 * A aba Histórico da campanha CPTu — onda 312, Tarefa 312.5.
 *
 * As linhas da trilha (a mesma `vw_trilha` que responde pela OS) da
 * campanha, das verticais — inclusive as substituídas —, das dissipações e
 * das fotos, da mais nova para a mais antiga: quem, quando, o que mudou
 * (antes → depois) e o motivo. As leituras e as curvas não entram: são o
 * bruto, que entra uma linha de trilha por leitura e não muda mais.
 *
 * Os valores vêm pelos dicionários da casa (agent-browser da 312): a
 * situação pela escada (`situacoes.ts`, «Substituída»), o encerramento pelo
 * rótulo do motivo, a vertical pelo código (a API manda os das verticais
 * citadas), o cone pelo patrimônio (e os equipamentos citados), o NA, a cota
 * e o pré-furo em metros com duas casas, a coordenada com seis, e as
 * premissas dizem quais chaves mudaram. A vertical que a campanha cancelou
 * aparece com a situação «Cancelada».
 *
 * Onda 313 (R12, revisão da 313.2): as camadas não se apagam, e o histórico
 * diz a entrada e a retirada de cada uma pelo trecho e pela unidade —
 * «camada 2,00 – 3,00 m · U2 entrou», «… foi retirada por Fulano» —, com quem a
 * retirou pelo nome (a API manda os nomes dos funcionários citados), nunca
 * o identificador.
 */
export function CampanhaHistorico({ c }: { c: CampanhaCompleta }) {
  const [linhas, setLinhas] = useState<LinhaDoHistorico[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    let vivo = true;
    historicoDaCampanha(c.campanha.id)
      .then((r) => { if (vivo) setLinhas(r); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [c.campanha.id, c.verticais.length, c.dissipacoes.length, c.campanha.situacao]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!linhas) return <Carregando texto="Carregando o histórico" />;
  if (linhas.length === 0) {
    return <section className="cartao"><Vazio>Nada registrado ainda nesta campanha.</Vazio></section>;
  }
  return (
    <section className="cartao">
      <div className="rola">
        <table aria-label="Histórico da campanha">
          <thead>
            <tr>
              <th scope="col">Quando</th>
              <th scope="col">Onde</th>
              <th scope="col">O que houve</th>
              <th scope="col" className="col-opcional">Quem</th>
              <th scope="col" className="col-opcional">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id}>
                <th scope="row"><span className="mono">{dataHora(l.datahora)}</span></th>
                <td>{onde(l)}</td>
                <td>
                  {oQueHouve(l)}
                  {l.motivo && <span className="cptu-motivo-estreito">{l.motivo}</span>}
                </td>
                <td className="col-opcional">
                  {l.usuario_nome ?? <span className="nada">o sistema</span>}
                  {l.perfil && <span className="sub">{rotulo(l.perfil)}</span>}
                </td>
                <td className="col-opcional">{l.motivo ?? <span className="nada">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="cptu-nota-da-tabela">
        {plural(linhas.length, 'registro', 'registros')} · o mais recente primeiro
        {linhas.length >= 500 && ' · os 500 últimos'}
      </p>
    </section>
  );
}

function onde(l: LinhaDoHistorico): string {
  switch (l.tabela) {
    case 'cptu_campanha': return 'Campanha';
    case 'cptu_vertical': return `Vertical ${l.vertical ?? ''}`.trim();
    case 'cptu_dissipacao':
      return `Dissipação ${l.vertical ?? ''}${l.profundidade !== null ? ` a ${numero(l.profundidade, 2)} m` : ''}`;
    case 'cptu_foto': return `Foto da vertical ${l.vertical ?? ''}`.trim();
    case 'cptu_camada': return `Camada da vertical ${l.vertical ?? ''}`.trim();
    default: return 'Registro';
  }
}

/** O nome do campo como a tela o diz. */
const CAMPO: { [campo: string]: string } = {
  situacao: 'situação', motivo_situacao: 'motivo', titulo: 'título', premissas: 'premissas',
  nivel_agua: 'NA', cota: 'cota', latitude: 'latitude', longitude: 'longitude',
  cone_equipamento_id: 'cone', cone_codigo: 'número do cone', motivo_parada: 'encerramento', motivo_parada_texto: 'encerramento (texto)',
  observacoes: 'observações', edicao: 'edição', substituida_por: 'substituída por', pre_furo_m: 'pré-furo',
  operador: 'operador', data_ensaio: 'data do ensaio',
  motivo_substituicao: 'motivo da substituição', vertical_id: 'vertical', legenda: 'legenda',
  no_relatorio: 'no relatório', ativa: 'na vertical', ordem: 'ordem', relatorio_id: 'relatório',
  topo_m: 'topo', base_m: 'base', unidade: 'unidade', descricao: 'descrição', origem: 'origem',
  parametros: 'parâmetros', retirada_em: 'retirada em', retirada_por: 'retirada por',
  u0_kpa: 'u₀', reta: 'reta', g0_mpa: 'G₀', su_kpa: 'Su', m_mpa: 'M',
  linhas_corte: 'linhas de corte', vista3d_arquivo_id: 'vista 3D', satelite_arquivo_id: 'imagem do terreno',
};

/** As linhas de corte (onda 316) pelo nome: «A–B, C–D»; «nenhuma» sem linha — nunca o JSON. */
function nomesDasLinhas(v: unknown): string {
  if (!Array.isArray(v) || v.length === 0) return 'nenhuma';
  return v.map((l) => (l && typeof l === 'object' && typeof (l as { nome?: unknown }).nome === 'string' ? (l as { nome: string }).nome : 'linha')).join(', ');
}

/** As marcas da tela (R16) mudam junto com as premissas e a edição: a linha diz a mudança, não a marca. */
const SO_MARCA = new Set(['premissas_da_tela_em', 'edicao_da_tela_em']);

const NOVO: { [tabela: string]: string } = {
  cptu_campanha: 'Campanha criada', cptu_vertical: 'Vertical importada',
  cptu_dissipacao: 'Dissipação importada', cptu_foto: 'Foto enviada',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Os campos em metros (duas casas, com a unidade) e em graus (seis casas). */
const EM_METROS = new Set(['nivel_agua', 'cota', 'pre_furo_m', 'profundidade_m', 'profundidade_final', 'topo_m', 'base_m']);
const EM_GRAUS = new Set(['latitude', 'longitude']);

/** Um valor da trilha sem despejar JSON nem identificador na tela — pelo dicionário do campo, quando há. */
function valor(v: unknown, campo?: string, l?: LinhaDoHistorico): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (campo && EM_METROS.has(campo) && Number.isFinite(Number(v))) return `${numero(v, 2)} m`;
  if (campo && EM_GRAUS.has(campo) && Number.isFinite(Number(v))) return numero(v, 6);
  if (typeof v === 'number') return numero(v, 3, true);
  if (typeof v === 'string') {
    if (UUID.test(v)) {
      if (campo === 'cone_equipamento_id') return l?.equipamentos?.[v]?.patrimonio ?? 'um cone do parque';
      if (campo === 'retirada_por') return l?.funcionarios?.[v] ?? 'um funcionário';
      return verticalCitada(v, l) ?? 'outro registro';
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return dataHora(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return dataDoDia(v);
    if (campo === 'situacao') {
      return degrauDe(l?.tabela === 'cptu_campanha' ? SITUACAO_DA_CAMPANHA_CPTU : SITUACAO_DA_VERTICAL_CPTU, v).texto;
    }
    if (campo === 'motivo_parada') return MOTIVOS_DE_PARADA.find(([m]) => m === v)?.[1] ?? rotulo(v);
    /* Outra chave de dicionário vira palavra; o texto livre fica como foi escrito. */
    return /^[a-z]+(_[a-z]+)*$/.test(v) ? rotulo(v) : v;
  }
  if (Array.isArray(v)) return plural(v.length, 'item', 'itens');
  return plural(Object.keys(v as object).length, 'valor', 'valores');
}

/**
 * A vertical citada pelo identificador, pelo código. A foto que passa para a vertical nova vai de um CPTu02 a outro
 * CPTu02: com o mesmo código dos dois lados, a importação de cada um diz qual é qual.
 */
function verticalCitada(id: string, l?: LinhaDoHistorico): string | null {
  const v = l?.verticais?.[id];
  if (!v) return null;
  const outro = Object.entries(l?.verticais ?? {}).some(([k, x]) => k !== id && x.codigo === v.codigo);
  return outro ? `${v.codigo} (importada em ${dataHora(v.importada_em)})` : v.codigo;
}

/** Uma premissa na frase: o número com vírgula, sim/não, a lista de pontos contada. */
function valorDaPremissa(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'number') return numero(v, 4, true);
  if (Array.isArray(v)) return plural(v.length, 'ponto', 'pontos');
  return String(v);
}

/** As chaves das premissas que mudaram, cada uma com o antes e o depois — e não «2 valores → 2 valores». */
function premissasQueMudaram(antes: unknown, depois: unknown): string {
  const a = (antes && typeof antes === 'object' ? antes : {}) as Record<string, unknown>;
  const d = (depois && typeof depois === 'object' ? depois : {}) as Record<string, unknown>;
  const chaves = [...new Set([...Object.keys(a), ...Object.keys(d)])]
    .filter((k) => JSON.stringify(a[k] ?? null) !== JSON.stringify(d[k] ?? null)).sort();
  if (!chaves.length) return 'sem mudança de valor';
  return chaves.map((k) => {
    const unidade = k === 'na' || k === 'cota' ? ' m' : '';
    const um = (x: unknown) => (typeof x === 'number' && unidade ? `${numero(x, 2)}${unidade}` : valorDaPremissa(x));
    return `${k} ${um(a[k])} → ${um(d[k])}`;
  }).join('; ');
}

/** A camada pelo trecho e pela unidade: «camada 2,00 – 3,00 m · U2» (a faixa como o documento, P-FMT-05). */
function textoDaCamada(l: LinhaDoHistorico): string {
  const k = l.camada;
  if (!k) return 'camada';
  return `camada ${faixaProfundidade(k.topo, k.base)} m · ${k.unidade}`;
}

function oQueHouve(l: LinhaDoHistorico) {
  if (l.tabela === 'cptu_camada') {
    const campos = l.campos_alterados ?? [];
    if (l.operacao === 'INSERT') return `${textoDaCamada(l)} entrou`;
    if (l.operacao === 'UPDATE' && campos.includes('retirada_em')) {
      const quem = valor(l.depois?.retirada_por, 'retirada_por', l);
      return `${textoDaCamada(l)} foi retirada${quem !== '—' ? ` por ${quem}` : ''}`;
    }
    if (l.operacao === 'UPDATE' && campos.includes('vertical_id')) {
      return `${textoDaCamada(l)} passou para a vertical nova: ${valor(l.antes?.vertical_id, 'vertical_id', l)} → ${valor(l.depois?.vertical_id, 'vertical_id', l)}`;
    }
  }
  if (l.operacao === 'INSERT') return NOVO[l.tabela] ?? 'Cadastro';
  if (l.operacao === 'DELETE') return 'Removido';
  const campos = (l.campos_alterados ?? []).filter((k) => !SO_MARCA.has(k));
  /* A gravação que regravou o mesmo valor (a trilha registra, sem campo mudado): diz isso, e não «Alterado». */
  if (!campos.length) return 'Regravado sem mudança de valor';
  return (
    <ul className="cptu-mudancas">
      {campos.map((k) => (
        <li key={k}>
          <b>{CAMPO[k] ?? 'outro campo'}</b>:{' '}
          {k === 'premissas' ? premissasQueMudaram(l.antes?.[k], l.depois?.[k])
            : k === 'linhas_corte' ? `${nomesDasLinhas(l.antes?.[k])} → ${nomesDasLinhas(l.depois?.[k])}`
              : `${valor(l.antes?.[k], k, l)} → ${valor(l.depois?.[k], k, l)}`}
        </li>
      ))}
    </ul>
  );
}
